'use client';

import type { PropsWithChildren } from 'react';

import { useSearchParams } from 'next/navigation';

import { type UiState } from 'instantsearch.js';
import { Configure } from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

import { type CategoryPageData } from '@/infra/services/search/get-facets/models';
import { useAnalyticsTags } from '@/presenters/hooks/algolia/useAnalyticsTags';
import { useGlobalStore } from '@/presenters/store/global';
import { ServerProvider } from '@/presenters/store/search/algolia-facets';
import {
  indexName,
  customSearchClient,
  insightsConfig,
  searchClient,
} from '@/utils/algolia';
import { entryKeyRouting } from '@/utils/algolia/router-proxy/entry-key';
import { algoliaStringFormatter } from '@/utils/algolia/router-proxy/entry-key/create-url/utils/algolia-string-formatter';

const HITS_PER_PAGE = 36;
const MAX_VALUES_PER_FACET = 1000;
const STALLED_SEARCH_DELAY = 2000;

interface AlgoliaSearchProviderProps extends PropsWithChildren {
  categoryPageData?: CategoryPageData | null;
  gaTrack?: string;
}

function AlgoliaSearchProvider(props: Readonly<AlgoliaSearchProviderProps>) {
  const { children, categoryPageData, gaTrack } = props;

  const { region } = useGlobalStore((store) => ({
    region: store.session.region,
  }));

  const defaultFilter = `regionalAttributes.${region}.promotionalPrice > 0 AND regionalAttributes.${region}.available=1 AND regionalAttributes.${region}.stock.hasStock=1`;
  const categoryFilter = `${defaultFilter} AND ${categoryPageData?.categoryPageID}`;

  const isCategory = Boolean(categoryPageData?.categoryPageID);

  const searchParams = useSearchParams();

  const analyticsTags = useAnalyticsTags(isCategory);

  const page = searchParams.get('page');

  type CategoryFiltersLabelProps = { [key: string]: string };

  const categoryFiltersLabel = Object.keys(
    categoryPageData?.algoliaFacets || {}
  ).reduce((acc: CategoryFiltersLabelProps, item: string) => {
    const key = algoliaStringFormatter(item);
    acc[key] = item;
    return acc;
  }, {});

  const algoliaFacetsObject = categoryPageData?.algoliaFacets;

  const routingCategory = entryKeyRouting(
    indexName,
    region,
    categoryFiltersLabel,
    algoliaFacetsObject
  ) as UiState;

  return (
    <ServerProvider
      value={{
        indexName,
        algoliaFacets: categoryPageData?.algoliaFacets,
        isCategoryPage: isCategory,
        gaTrack: gaTrack || '',
      }}
    >
      <InstantSearchNext
        indexName={indexName}
        insights={insightsConfig}
        routing={isCategory && routingCategory}
        searchClient={isCategory ? searchClient : customSearchClient}
        future={{ preserveSharedStateOnUnmount: true }}
        stalledSearchDelay={STALLED_SEARCH_DELAY}
      >
        {region && (
          <Configure
            facets={['*']}
            filters={isCategory ? categoryFilter : defaultFilter}
            hitsPerPage={HITS_PER_PAGE}
            analyticsTags={analyticsTags}
            maxValuesPerFacet={MAX_VALUES_PER_FACET}
            page={Math.max(Number(page) - 1, 0) || 0}
            analytics
            clickAnalytics
            facetingAfterDistinct
          />
        )}

        {children}
      </InstantSearchNext>
    </ServerProvider>
  );
}

export default AlgoliaSearchProvider;
